import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowLeft, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/auth/request-otp`, { email });
      // Redirect to OTP verification page with email and resend_token in state
      navigate('/verify-otp', { state: { email, resend_token: data.resend_token } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
      {/* Background Aura Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#D0021B]/10 blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px]"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[450px]"
      >
        <div className="group relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.02] to-transparent pointer-events-none"></div>
          
          {loading && (
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              className="absolute top-0 left-0 h-1 bg-[#D0021B] shadow-[0_0_10px_#D0021B] z-20"
            />
          )}

          <div className="px-8 pt-12 pb-10">
            <div className="text-center mb-10">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex p-4 rounded-3xl bg-white/[0.05] border border-white/10 mb-6"
              >
                <Mail className="w-8 h-8 text-[#D0021B]" />
              </motion.div>
              
              <h1 className="font-serif text-3xl font-bold text-white tracking-tight mb-2 uppercase">
                Reset Password
              </h1>
              <p className="text-white/40 text-sm tracking-wide uppercase font-bold">
                Enter your email to receive an OTP
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 overflow-hidden"
                >
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200 text-sm flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused || email ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  EMAIL ADDRESS
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className={`w-5 h-5 transition-colors duration-300 ${isFocused ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={!isFocused ? "e.g. admin@factindia.com" : ""}
                    className="w-full pl-12 pr-4 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    required
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-[#D0021B] text-white font-bold rounded-2xl shadow-[0_8px_32px_rgba(208,2,27,0.3)] hover:shadow-[0_12px_48px_rgba(208,2,27,0.45)] transition-all disabled:opacity-50 group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                <div className="flex items-center justify-center gap-2">
                  {loading ? 'SENDING OTP...' : 'SEND OTP CODE'}
                  {!loading && <Send className="w-4 h-4" />}
                </div>
              </motion.button>
            </form>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-center"
            >
              <Link to="/login" className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors text-sm group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to Login
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
